import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Key, ArrowRight, Check, LogIn, UserPlus, Mail, Shield, AlertTriangle, Inbox } from "lucide-react";

export default function ClaimPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialCode = searchParams.get("code") || "";
  const [accessCode, setAccessCode] = useState(initialCode);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const storedCodes = []; // No longer store claim codes in localStorage

  const handleClaim = async () => {
    if (!accessCode.trim()) {
      toast.error("Please enter an access code");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/free/claim-alias", { claim_code: accessCode.trim() });
      toast.success("Email linked to your account!");
      setSuccess(data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error(formatApiError(err));
      } else if (status === 410) {
        toast.error("This access code has expired.");
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAccessFree = () => {
    if (!accessCode.trim()) {
      toast.error("Please enter an access code");
      return;
    }
    navigate(`/free/${accessCode.trim()}`);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4" data-testid="claim-success">
        <div className="max-w-sm text-center space-y-5">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-green-50 flex items-center justify-center">
            <Check size={28} className="text-green-600" />
          </div>
          <h1 className="font-heading text-2xl font-medium text-[#111111]">Email linked</h1>
          <p className="text-sm text-zinc-500">
            Your email has been linked to your account.
            {success.emails_migrated > 0 && ` ${success.emails_migrated} email${success.emails_migrated > 1 ? "s" : ""} transferred.`}
          </p>
          {success.alias_address && (
            <div className="p-3 rounded-md bg-[#F5F5F5] border border-[#E5E5E5]">
              <span className="text-sm text-[#888888]" data-testid="claim-success-address">{success.alias_address}</span>
            </div>
          )}
          <Link to="/inbox" data-testid="claim-success-dashboard">
            <Button className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white">
              Go to Dashboard <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="claim-page">
      <header className="border-b border-[#E5E5E5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/?new=1" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-blue-600 flex items-center justify-center">
              <Mail size={14} className="text-white" strokeWidth={2} />
            </div>
            <span className="font-heading text-lg font-medium tracking-tight text-[#111111]">QuickInbox</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" size="sm" className="text-zinc-500 text-sm">Sign in</Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-md mx-auto px-4 py-16 space-y-8">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
            <Inbox size={24} className="text-blue-600" />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-[#111111]" data-testid="claim-title">
            Access your inbox
          </h1>
          <p className="mt-2 text-sm text-zinc-500">
            Enter the access code you received when creating your email.
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm text-[#555555]">Access Code</Label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
              <Input
                placeholder="XXXX-XXXX-XXXX"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                className="pl-10 h-12 sm:h-11 font-mono tracking-wider text-center text-lg"
                data-testid="access-code-input"
                onKeyDown={(e) => e.key === "Enter" && handleAccessFree()}
              />
            </div>
          </div>

          {storedCodes.length > 0 && (
            <div className="space-y-2" data-testid="stored-codes-section">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">Your recent inboxes</p>
              {storedCodes.map((item) => (
                <button
                  key={item.code}
                  onClick={() => setAccessCode(item.code)}
                  className="w-full text-left p-2.5 rounded-md border border-[#E5E5E5] hover:border-[#E5E5E5] hover:bg-[#F5F5F5] transition-colors"
                  data-testid={`stored-code-${item.code}`}
                >
                  <p className="text-xs font-mono font-medium text-[#555555]">{item.code}</p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">{item.address}</p>
                </button>
              ))}
            </div>
          )}

          <div className="space-y-2 pt-2">
            <Button
              onClick={handleAccessFree}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white"
              disabled={!accessCode.trim()}
              data-testid="access-inbox-button"
            >
              <Inbox size={16} className="mr-2" /> Open my inbox
            </Button>

            {user ? (
              <Button
                onClick={handleClaim}
                variant="outline"
                className="w-full h-11"
                disabled={loading || !accessCode.trim()}
                data-testid="claim-submit-button"
              >
                {loading ? "Linking..." : <><Key size={16} className="mr-2" /> Link to my account</>}
              </Button>
            ) : (
              <div className="p-3 rounded-md bg-[#F5F5F5] border border-[#E5E5E5] text-center space-y-2">
                <p className="text-xs text-[#888888]">Want to keep this email permanently? Sign in or create an account.</p>
                <div className="flex gap-2 justify-center">
                  <Link to="/login" state={{ returnTo: "/claim" }}>
                    <Button size="sm" variant="outline" className="text-xs">
                      <LogIn size={13} className="mr-1" /> Sign in
                    </Button>
                  </Link>
                  <Link to={accessCode.trim() ? `/register?claim_code=${encodeURIComponent(accessCode.trim())}` : "/register"} state={{ returnTo: "/claim", claimCode: accessCode.trim() }}>
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs">
                      <UserPlus size={13} className="mr-1" /> Register & link
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>

          <div className="p-3 rounded-md bg-[#F5F5F5] border border-[#E5E5E5] text-xs text-zinc-500 space-y-1" data-testid="access-info">
            <p className="flex items-center gap-1.5"><Shield size={12} className="text-zinc-500" /> Access codes are valid for 7 days after creation</p>
            <p className="flex items-center gap-1.5"><AlertTriangle size={12} className="text-zinc-500" /> Too many failed attempts will temporarily lock the code</p>
          </div>
        </div>
      </div>
    </div>
  );
}
